import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  AtmosphericParticles,
  DynamicSubtitle,
  SpecularShine,
  WeeklyPass3DBadge,
} from "./components";

export const KyuStoreElementsAction: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Palette: Clean Esport Studio Light Theme
  const bgCanvas = "#F8F6FE";
  const bgCard = "#FFFFFF";
  const kyuPurple = "#5D3FD3";
  const textDark = "#0F172A";
  const textMuted = "#64748B";
  const emeraldSuccess = "#10B981";
  const emeraldLight = "#ECFDF5";

  const EASE = Easing.bezier(0.16, 1, 0.3, 1);

  const sp = (delay: number, damping = 14, stiffness = 125) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Camera 3D Parallax
  const camIntro = sp(4, 15, 110);
  const rotYIntro = interpolate(camIntro, [0, 1], [-16, -4]);
  const rotXIntro = interpolate(camIntro, [0, 1], [10, 4]);

  const orbitRotY = Math.sin((frame - 60) / 26) * 4.5;
  const orbitRotX = Math.cos((frame - 60) / 28) * 2.5;

  // Dolly push-in on payoff beat (Frame 160 - 200)
  const pushIn = interpolate(frame, [155, 195], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: EASE,
  });
  const pushZ = interpolate(pushIn, [0, 1], [0, 110]);
  const pushScale = interpolate(pushIn, [0, 1], [1, 1.06]);
  const pushRotY = interpolate(pushIn, [0, 1], [0, 6]);

  const currentRotY = (frame < 60 ? rotYIntro : -4 + orbitRotY) + pushRotY;
  const currentRotX = frame < 60 ? rotXIntro : 4 + orbitRotX;
  const currentScale = pushScale;

  // Remotion Elements: Specular Shine progress (sweeping from 0 to 1 twice across the video)
  const shineProgress1 = interpolate(frame, [25, 80], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: EASE,
  });
  const shineProgress2 = interpolate(frame, [155, 210], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: EASE,
  });
  const currentShineProgress = frame < 120 ? shineProgress1 : shineProgress2;

  // Floating Hero Float
  const heroFloatY = Math.sin(frame / 20) * 5;

  // Subtitles (Raised to optimal safe band: bottom: 400px)
  const SUBTITLES = [
    { from: 0, to: 100, text: "Di Kyu Store, top up diamond Mobile Legends resmi seratus persen...", highlight: "resmi seratus persen" },
    { from: 100, to: 170, text: "...aman anti-minus akun kamu...", highlight: "anti-minus akun kamu" },
    { from: 170, to: 246, text: "...dan langsung masuk dalam satu detik!", highlight: "dalam satu detik!" },
  ];

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCanvas,
        fontFamily: "'Rajdhani', -apple-system, sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* 1. Master Audio with Synchronized SFX */}
      <Audio src={staticFile("kyustore_demo_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.12} />
      {frame === 6 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.65} />}
      {frame === 38 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 160 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.7} />}
      {frame === 180 && <Audio src={staticFile("ui-success.mp3")} volume={0.75} />}

      {/* Multi-Depth Background (Z: -80px) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* Volumetric Center Ambient Glow */}
      <div
        style={{
          position: "absolute",
          top: "46%",
          left: "50%",
          width: 880,
          height: 880,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.14) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      {/* Top Corner HUD */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 90,
          display: "flex",
          alignItems: "center",
          gap: 12,
          zIndex: 50,
        }}
      >
        <Img src={staticFile("kyu_logo.png")} style={{ height: 34, objectFit: "contain" }} />
        <div style={{ width: 1.5, height: 22, backgroundColor: "#CBD5E1" }} />
        <span
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: textMuted,
          }}
        >
          [ OFFICIAL MOONTON DISTRIBUTOR ]
        </span>
      </div>

      <div
        style={{
          position: "absolute",
          top: 80,
          right: 90,
          display: "flex",
          alignItems: "center",
          gap: 8,
          backgroundColor: bgCard,
          border: `1.5px solid ${emeraldSuccess}`,
          padding: "6px 18px",
          borderRadius: 20,
          boxShadow: "0 4px 15px rgba(16, 185, 129, 0.12)",
          zIndex: 50,
        }}
      >
        <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: emeraldSuccess, boxShadow: "0 0 8px #10B981" }} />
        <span
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            color: emeraldSuccess,
            letterSpacing: "0.08em",
          }}
        >
          24/7 AUTO INJECT ACTIVE
        </span>
      </div>

      {/* ========================================================================= */}
      {/* 3D SPATIAL VIEWPORT CONTAINER (Perspective 1400px)                         */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1400,
          perspectiveOrigin: "50% 48%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {/* Master Spatial Stage (Preserve-3D) */}
        <div
          style={{
            position: "relative",
            width: 900,
            height: 1100,
            transformStyle: "preserve-3d",
            transform: `rotateY(${currentRotY}deg) rotateX(${currentRotX}deg) translateZ(${pushZ}px) scale(${currentScale})`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Headline (Floating at z: 40px) */}
          <div
            style={{
              transform: "translateZ(40px)",
              textAlign: "center",
              marginBottom: 20,
              zIndex: 10,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                backgroundColor: emeraldLight,
                border: `1.5px solid ${emeraldSuccess}`,
                padding: "6px 20px",
                borderRadius: 20,
                marginBottom: 12,
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.14)",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
              </svg>
              <span
                style={{
                  fontFamily: "'Chakra Petch', sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  color: emeraldSuccess,
                  letterSpacing: "0.08em",
                }}
              >
                100% GARANSI ANTI-MINUS RESMI
              </span>
            </div>

            <h1
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 66,
                fontWeight: 400,
                lineHeight: 1.02,
                letterSpacing: "0.02em",
                textShadow: "0 10px 30px rgba(0,0,0,0.06)",
              }}
            >
              TOP UP MOBILE LEGENDS <br />
              <span style={{ color: kyuPurple }}>SATSET, AMAN & RESMI DI KYU STORE!</span>
            </h1>
          </div>

          {/* ========================================================================= */}
          {/* PLANE 0 (z: 0px): ELEVATED 3D STAGE WITH SHINE EFFECT & GAMEPLAY          */}
          {/* ========================================================================= */}
          <div
            style={{
              position: "relative",
              width: 860,
              height: 640,
              backgroundColor: "rgba(255, 255, 255, 0.94)",
              backdropFilter: "blur(24px) saturate(180%)",
              borderRadius: 38,
              border: `2.5px solid ${kyuPurple}`,
              boxShadow: "0 35px 90px rgba(93, 63, 211, 0.2), 0 10px 30px rgba(0,0,0,0.06)",
              transformStyle: "preserve-3d",
              transform: `translateZ(0px) translateY(${heroFloatY}px)`,
              overflow: "visible", // Breaking bounds
            }}
          >
            {/* ===================================================================== */}
            {/* REMOTION ELEMENT #2: SPECULAR SHINE SWEEP (commerce/shine)            */}
            {/* Projects an optical diagonal glass glare sweep across the card        */}
            {/* ===================================================================== */}
            <SpecularShine
              progress={currentShineProgress}
              angle={28}
              coreWidth={95}
              haloWidth={280}
              coreIntensity={0.92}
              haloIntensity={0.38}
              borderRadius={38}
            />

            {/* Inner Real MLBB In-Game Gameplay Screenshot Frame */}
            <div
              style={{
                position: "absolute",
                inset: 16,
                borderRadius: 28,
                backgroundColor: "#0B041E",
                overflow: "hidden",
                transform: "translateZ(10px)",
                boxShadow: "inset 0 2px 20px rgba(0, 0, 0, 0.4)",
              }}
            >
              {/* Authentic In-Game MLBB Screenshot (Gusion Soul Revelation Casting Skill 2) */}
              <Img
                src={staticFile("mlbb_ingame_gusion.jpg")}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center 30%",
                  filter: "contrast(1.05) saturate(1.1)",
                }}
              />

              {/* Edge Glow */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  boxShadow: "inset 0 0 45px rgba(93, 63, 211, 0.4), inset 0 0 20px rgba(0, 240, 255, 0.25)",
                  pointerEvents: "none",
                }}
              />

              {/* In-Game Tag Badge */}
              <div
                style={{
                  position: "absolute",
                  top: 18,
                  left: 20,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  backgroundColor: "rgba(18, 10, 46, 0.85)",
                  backdropFilter: "blur(8px)",
                  padding: "6px 14px",
                  borderRadius: 14,
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#00F0FF", boxShadow: "0 0 8px #00F0FF" }} />
                <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 13, fontWeight: 700, color: "#FFFFFF", letterSpacing: "0.06em" }}>
                  IN-GAME MLBB SHOWCASE
                </span>
              </div>
            </div>

            {/* ========================================================================= */}
            {/* PLANE 2 (z: 110px): FLOATING 3D WEEKLY DIAMOND PASS BADGE (LEFT)          */}
            {/* ========================================================================= */}
            <WeeklyPass3DBadge
              price="Rp 27.500"
              originalPrice="Rp 35.000"
              tag="BEST VALUE"
              translateZ={110}
              rotateY={12}
            />

            {/* ========================================================================= */}
            {/* PLANE 2 (z: 95px): FLOATING TRUST PILL (RIGHT)                            */}
            {/* ========================================================================= */}
            <div
              style={{
                position: "absolute",
                bottom: 70,
                right: -30,
                transformStyle: "preserve-3d",
                transform: "translateZ(95px) rotateY(-10deg)",
                backgroundColor: bgCard,
                borderRadius: 24,
                border: `2px solid ${emeraldSuccess}`,
                padding: "16px 22px",
                boxShadow: "0 18px 40px rgba(16, 185, 129, 0.2), 0 8px 15px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 12,
                zIndex: 35,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  backgroundColor: emeraldLight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                </svg>
              </div>

              <div>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: emeraldSuccess, letterSpacing: "0.02em" }}>
                  CUKUP USER ID & ZONE ID
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 800, color: textDark }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="2.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <span>100% Tanpa Password Akun</span>
                </div>
              </div>
            </div>

            {/* Bottom Bar inside Card: Instant Inject Callout */}
            <div
              style={{
                position: "absolute",
                bottom: 18,
                left: 20,
                right: 20,
                backgroundColor: "rgba(255, 255, 255, 0.94)",
                backdropFilter: "blur(14px)",
                border: "1.5px solid #E2E8F0",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)",
                borderRadius: 20,
                padding: "12px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                transform: "translateZ(30px)",
                zIndex: 20,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: emeraldSuccess, boxShadow: `0 0 8px ${emeraldSuccess}` }} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 800, color: textDark }}>
                  Realtime Moonton API Integration
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 13, fontWeight: 700, color: kyuPurple }}>
                  PROSES 1 DETIK SATSET
                </span>
              </div>
            </div>
          </div>

          {/* 18 Atmospheric Particles with DoF Blur */}
          <AtmosphericParticles frame={frame} count={18} />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* CORNER FOOTER TICKER (Safe Zone Bottom 80px)                              */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 90,
          right: 90,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: bgCard,
          border: "1.5px solid #E2E8F0",
          borderRadius: 20,
          padding: "12px 28px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 800, color: textMuted }}>
          <span style={{ color: kyuPurple }}>METODE RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'Chakra Petch', sans-serif", fontSize: 13, fontWeight: 700, color: emeraldSuccess }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>MOONTON DIRECT INJECT</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: RAISED TO OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      <DynamicSubtitle frame={frame} subtitles={SUBTITLES} bottom={400} />
    </AbsoluteFill>
  );
};
